import { useState, useEffect, Fragment, useMemo } from 'react';
import { Card, Snackbar, Snack } from '@nike/eds';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useSearchParams, useRevalidator } from 'react-router-dom';
import productWallClient from '../../clientAPI/productwall';
import { useInView } from 'react-intersection-observer';
import Loader from 'components/Loader';
import ProductWallItem from 'components/ProductWall/ProductWallItem';
import {
  productWallErrorMessageAtom,
  productWallHasErrorAtom,
} from '../../atoms/productwall';
import { useAtom } from 'jotai';
import { Explain } from 'components/Explain/Explain';
import { Filter, FilterBreadcrumbs } from 'components/ProductWall/Filter';
import parseArrayFromUrlString from '../../utils/parseArrayFromUrlString';
import propagate403Error from '../../utils/propagate403Error';
import Dropdown from 'components/Dropdown/Dropdown';
import { PWALL_SORT_OPT_PARAMS } from 'constants/index';

const sortOptions = [
  { label: '精选推荐', value: '' },
  { label: '最新', value: 'newest' },
  { label: '价格：由高到低', value: 'priceDesc' },
  { label: '价格：由低到高', value: 'priceAsc' },
];

export default function ProductWallResults({
  searchTerms,
  consumerChannelId,
  language,
  marketplace,
  callerId,
  userAgent,
  searchOptions,
  searchOptionsForExplainQuery,
  showExplainPanel,
  taxonomyIds,
  setTaxonomyIds,
}: {
  searchTerms: string;
  consumerChannelId: string;
  language: string;
  marketplace: string;
  callerId: string;
  userAgent: string;
  searchOptions: keyable;
  searchOptionsForExplainQuery: keyable;
  showExplainPanel: string;
  taxonomyIds: Array<string>;
  setTaxonomyIds: (taxonomyIds: Array<string>) => void;
}) {
  const revalidator = useRevalidator();
  const [searchParams, setSearchParams] = useSearchParams();

  const taxonomyIdsUpdater = (val: Array<string>) => {
    setTaxonomyIds(val);
    if (val.length === 0) {
      searchParams.set('taxonomyIds', '');
    } else {
      const urlVal = encodeURIComponent(JSON.stringify(val));
      searchParams.set('taxonomyIds', urlVal);
    }
    setSearchParams(searchParams);
  };
  const [nextMsg, setNextMsg] = useState('');

  const [errorMsg, setErrorMsg] = useAtom(productWallErrorMessageAtom);
  const [hasError, setHasError] = useAtom(productWallHasErrorAtom);

  const [sortBy, setSortBy] = useState(sortOptions[0].value);
  const [selectedOptionIdx, setSelectedOptionIdx] = useState(0);
  const handleSortSelection = (opt: { value: string }) => {
    const i = sortOptions.findIndex((o) => o.value === opt.value);
    setSelectedOptionIdx(i);
    setSortBy(opt.value);
  };
  const sortParamVal = useMemo(() => {
    return sortBy !== ''
      ? PWALL_SORT_OPT_PARAMS[sortBy as keyof typeof PWALL_SORT_OPT_PARAMS]
      : '';
  }, [sortBy]);
  const {
    data,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useInfiniteQuery({
    queryKey: [
      searchTerms,
      consumerChannelId,
      language,
      marketplace,
      callerId,
      userAgent,
      searchOptions,
      taxonomyIds,
      sortBy,
    ],
    getNextPageParam: (lastPage: {
      products: WallItem[];
      pages: RollupPages;
    }) => lastPage.pages.next || undefined,
    queryFn: async ({ pageParam = '' }) => {
      return productWallClient.loadNextPage({
        anchor: 0,
        count: 30,
        searchTerms: searchTerms,
        language: language,
        marketplace: marketplace,
        headerCallerId: callerId,
        headerUserAgent: userAgent,
        consumerChannelId: consumerChannelId,
        searchOptions,
        taxonomyIds,
        next: pageParam,
        sort: sortParamVal,
      });
    },
    onSuccess: (_data) => {
      setNextMsg('No more data');
    },
    onError: (err: keyable) => {
      setErrorMsg(err.message || JSON.stringify(err));
      setHasError(true);
      setNextMsg('Error');
      err.status === 401 && revalidator.revalidate();
    },
  });
  propagate403Error(error as keyable);

  const { ref, inView } = useInView();
  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage]);

  return (
    <div>
      <div className='tw-relative'>
        <div className='tw-flex tw-justify-between'>
          <p className='eds-type--display-5 tw-mb-5'>
            Total:{' '}
            {isFetching
              ? 'Loading'
              : data?.pages?.[0].pages.totalResources || 0}
          </p>
          <div>
            <Dropdown
              anchor={'right'}
              defaultText={'排序依据'}
              onDropDownSelect={handleSortSelection}
              options={sortOptions}
              selectedItemPrefex={`排序依据: `}
              selectedOption={selectedOptionIdx}
            />
          </div>
        </div>
        <div className='tw-mb-1'>
          <FilterBreadcrumbs
            channelId={consumerChannelId}
            language={language}
            marketplace={marketplace}
            taxonomyIds={taxonomyIds}
            searchTerms={searchTerms}
            searchOptions={searchOptions}
            onChange={taxonomyIdsUpdater}
            callerId={callerId}
            userAgent={userAgent}
          />
        </div>
        <div className='tw-flex tw-relative tw-gap-1'>
          <div
            style={{ flex: '0 0 240px' }}
            className='tw-pb-4 tw-pr-4 tw-max-h-screen tw-overflow-y-scroll tw-sticky tw-top-0'
          >
            <Filter
              channelId={consumerChannelId}
              language={language}
              marketplace={marketplace}
              taxonomyIds={taxonomyIds}
              searchTerms={searchTerms}
              searchOptions={searchOptions}
              onChange={taxonomyIdsUpdater}
              callerId={callerId}
              userAgent={userAgent}
            />
          </div>
          <div className='tw-flex tw-flex-wrap tw-gap-5 tw-content-start'>
            {data?.pages.map((page) => {
              return (
                <Fragment key={page.pages?.next}>
                  {page.products.map((p) => (
                    <ProductWallItem key={p.id} data={p} />
                  ))}
                </Fragment>
              );
            })}
            {(isFetching || isFetchingNextPage) && (
              <div className='tw-w-full tw-flex tw-justify-center tw-items-center'>
                <Loader />
              </div>
            )}
            <div className='tw-h-2' ref={ref} />
            {!hasNextPage && !isFetchingNextPage && !isFetching && (
              <div className='tw-w-full tw-flex tw-justify-center tw-items-center'>
                <p className='tw-text-gray-500'>{nextMsg}</p>
              </div>
            )}
          </div>
        </div>
        {showExplainPanel === 'true' && (
          <div className='tw-absolute tw-right-0 tw-top-0 tw-max-w-full tw-overflow-auto tw-bg-gray-100 eds-elevation--4'>
            <Card className='tw-px-12'>
              <Explain
                searchTerms={searchTerms}
                marketplace={marketplace}
                language={language}
                consumerChannelId={consumerChannelId}
                searchOptions={searchOptionsForExplainQuery}
                sort={sortParamVal}
              />
            </Card>
          </div>
        )}
      </div>
      <Snackbar style={{ left: '90%', top: '90%' }}>
        {errorMsg && (
          <Snack
            autoDismissDuration={5000}
            id='pw-error-notification'
            onDismiss={(_id) => {
              setErrorMsg('');
            }}
            status={hasError ? 'error' : 'success'}
          >
            {errorMsg}
          </Snack>
        )}
      </Snackbar>
    </div>
  );
}
